import { mutationOptions } from '@tanstack/react-query';

import { isStandbyPublicEntryGone } from '@/api/standby/standbyPublicEntryQuery';
import apiClient from '@/constants/http';
import { extractApiError } from '@/hooks/mutations/utils';
import { readStatusCode } from '@/utils/apiErrors';
import { classifyCheckoutError } from '@/utils/checkoutErrors';
import { Logger } from '@/utils/logging';

import type {
  StandbyWaitlistJoinError,
  StandbyWaitlistJoinFailureReason,
  StandbyWaitlistJoinMutationOptions,
  StandbyWaitlistJoinPayload,
  StandbyWaitlistJoinResponse,
} from './types';

export const STANDBY_WAITLIST_JOIN_ERROR = 'STANDBY_WAITLIST_JOIN_ERROR';

const ALREADY_ADDED_CODE = 'standby-list.entry.already-added';
const THROTTLED_CODE = 'standby-list.auth.throttled';

const readErrorCode = (error: unknown): string | undefined => {
  if (typeof error !== 'object' || error === null) return undefined;
  const { code } = error as { code?: unknown };
  return typeof code === 'string' ? code : undefined;
};

/**
 * Works on both the raw rejection and the normalized error (code + statusCode
 * are preserved on throw).
 */
export const getStandbyWaitlistJoinFailureReason = (
  error: unknown,
): StandbyWaitlistJoinFailureReason => {
  const code = readErrorCode(error);
  const status = readStatusCode(error);

  if (code === ALREADY_ADDED_CODE || status === 409) return 'duplicate';
  if (code === THROTTLED_CODE || status === 429) return 'throttled';
  if (isStandbyPublicEntryGone(error)) return 'unavailable';
  return 'other';
};

const standbyWaitlistJoin = async ({
  shopId,
  requestedBarberId,
  ...rest
}: StandbyWaitlistJoinPayload): Promise<StandbyWaitlistJoinResponse> => {
  // Omitted (not undefined) so "any barber" sends no requestedBarberId at all.
  const body = requestedBarberId ? { ...rest, requestedBarberId } : rest;

  try {
    const { data } = await apiClient.post<StandbyWaitlistJoinResponse>(
      `/v1/standby-list/public/${encodeURIComponent(shopId)}/waitlist-join`,
      body,
      { timeout: 20000 },
    );

    return data;
  } catch (error) {
    const apiError = extractApiError(error, STANDBY_WAITLIST_JOIN_ERROR);

    // Code only: the raw error can echo the guest's phone/email/name.
    Logger.error('Error joining standby waitlist', { code: apiError.code });

    // statusCode and the failure kind are read off the raw error here because
    // the rethrow is a plain object, never the AxiosError (its config has PII).
    const joinError: StandbyWaitlistJoinError = {
      ...apiError,
      statusCode: readStatusCode(error),
      checkoutErrorType: classifyCheckoutError(error),
    };
    throw joinError;
  }
};

export const standbyWaitlistJoinMutation = (
  options?: StandbyWaitlistJoinMutationOptions,
) =>
  mutationOptions({
    ...options,
    mutationKey: ['standby', 'waitlistJoin'],
    mutationFn: standbyWaitlistJoin,
    // A retried join would hit the duplicate guard after the first succeeded.
    retry: false,
    // Never pause or persist: the variables/result carry the one-time leave
    // token and guest details, and a paused call would fire later on its own.
    networkMode: 'always',
    meta: { persist: false },
    gcTime: 0,
  });
